import React, { useState, useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { get } from 'lodash';
import { RootState } from 'modules';
import { verifyPayment, createPaymentLog } from 'libs/api/mentoring';
import { commifyFormatter } from 'libs/validators';
import { TModalProps } from 'modules/types';
import { CPlanPrice } from 'constants/mentoring.const';
import { EPayMethod } from 'enums/base.enum';
import { Checkbox, Input, Button, Col, Row, Typography, Form, Divider, Modal } from 'antd';
import styled from 'styled-components';
import { FormWrapper, LightGrayColor } from 'GlobalStyles';

const { Title } = Typography;
const CreditPurchaseWrapper = styled.div`
  .ant-form-item {
    margin-bottom: 12px;
  }
  .info-box {
    font-size: 14px;
    color: #000;
    padding: 20px;
    background-color: ${LightGrayColor};
    border-radius: 8px;
    .ant-divider-horizontal {
      margin: 8px 0 !important;
    }
  }
`;
const CreditPurchaseModal = ({ modalState, setModalState }: TModalProps) => {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const planIndex = modalState.record.planIndex;
  const count = modalState.record.count;
  const amount = count * get(CPlanPrice[planIndex], 'price');
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(modalState.visible);
  }, [modalState.visible]);

  function handleCancel() {
    setModalState({ visible: false, record: {} });
  }

  async function onClickPayment(planIndex: number) {
    /* 1. 가맹점 식별하기 */
    const { IMP } = window as any;
    IMP.init('imp73372599');

    /* 2. 결제 데이터 정의하기 */
    const data = {
      pg: 'html5_inicis',
      pay_method: form.getFieldValue('pay_method'),
      name: '수강권 구매',
      amount: 0,
      buyer_email: form.getFieldValue('email'),
      buyer_name: form.getFieldValue('fullname'),
      buyer_tel: form.getFieldValue('phone'),
      merchant_uid: '',
    };

    /* backend에서 결제정보 생성하고 merchant_id 받아오기 */
    try {
      const response = await createPaymentLog({
        plan_index: planIndex,
        pay_method: get(data, 'pay_method'),
        credit: count /* credit을 front에서 바로 보내주면 보안 취약할듯. 수정하기 */,
      });
      data.amount = get(response, 'data.amount');
      data.merchant_uid = get(response, 'data.merchant_uid');
    } catch (e) {
      throw e;
    }

    async function callback(rsp: any) {
      setModalState({ visible: false, record: {} });
      /* backend에 imp_uid와 merchant_uid 전달 */
      try {
        await verifyPayment({
          result: rsp.success,
          error_message: rsp.error_msg,
          imp_uid: rsp.imp_uid,
          merchant_uid: get(data, 'merchant_uid'),
        });
        modalState.onFinish && modalState.onFinish();
      } catch (e) {
        throw e;
      }
    }

    /* 4. 결제 창 호출하기 */
    IMP.request_pay(data, callback);
  }

  return (
    <Modal
      visible={visible}
      centered={true}
      footer={null}
      maskClosable={true}
      closable={true}
      width={500}
      onCancel={handleCancel}
    >
      <CreditPurchaseWrapper>
        <FormWrapper style={{ textAlign: 'left' }}>
          <Form
            form={form}
            colon={false}
            layout='horizontal'
            className='form-normal'
            labelCol={{ flex: '100px' }}
            wrapperCol={{ flex: 'auto' }}
            labelAlign='left'
            initialValues={{
              fullname: get(currentUser, 'fullname'),
              phone: get(currentUser, 'phone'),
              email: get(currentUser, 'userid'),
              pay_method: EPayMethod.Card,
            }}
          >
            <Title level={4}>구매자 정보</Title>
            <Form.Item label='주문자' name='fullname' className='form-item' rules={[{ required: true }]}>
              <Input placeholder='주문자 이름을 입력해주세요.' maxLength={100} />
            </Form.Item>
            <Form.Item label='휴대폰 번호' name='phone' className='form-item' rules={[{ required: true }]}>
              <Input placeholder='휴대폰 번호를 입력해주세요.' maxLength={100} />
            </Form.Item>
            <Form.Item label='이메일' name='email' className='form-item' rules={[{ required: true }]}>
              <Input placeholder='이메일을 입력해주세요.' maxLength={100} />
            </Form.Item>
            <Title level={4} style={{ marginTop: '45px' }}>
              결제 금액
            </Title>
            <div className='info-box'>
              <Row justify='space-between'>
                <Col>상품명</Col>
                <Col>{count}회 수강권</Col>
              </Row>
              <Divider />
              <Row justify='space-between'>
                <Col>수량</Col>
                <Col>1</Col>
              </Row>
              <Divider />
              <Row justify='space-between'>
                <Col>상품 금액</Col>
                <Col>{commifyFormatter(`${amount}`)}원</Col>
              </Row>
              <Divider />
              <Row
                justify='space-between'
                style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#3cac40',
                }}
              >
                <Col>최종 결제 금액</Col>
                <Col>{commifyFormatter(`${amount}`)}원</Col>
              </Row>
            </div>
            <Form.Item
              name={'agreement_policy'}
              className='form-bottom'
              valuePropName={'checked'}
              style={{ marginTop: '15px' }}
            >
              <Checkbox style={{ fontSize: '14px', width: '100%' }}>
                결제 내역을 확인하였으며 전자상거래법 및 커리어스텝 정책에 의거하여 환불이 진행되는 것에
                동의합니다.
              </Checkbox>
            </Form.Item>
            <Form.Item shouldUpdate={true} noStyle={true}>
              {() => (
                <Button
                  type='primary'
                  size='large'
                  style={{ float: 'right', marginTop: '20px' }}
                  onClick={() => {
                    onClickPayment(planIndex);
                  }}
                  disabled={!form.getFieldValue('agreement_policy')}
                >
                  결제하기
                </Button>
              )}
            </Form.Item>
          </Form>
        </FormWrapper>
      </CreditPurchaseWrapper>
    </Modal>
  );
};

export default CreditPurchaseModal;
